import Config from '@config';

import { injectBmsSlice } from '@/app/bmsApi';
import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { infiniteQueryOptions } from '@/features/utils';

import { FiltersGrid, Pagination } from '../types';
import {
  ViolationAct,
  ViolationActDecisionRequestBody,
  ViolationActDevice,
  ViolationActModem,
  ViolationActResultRequestBody,
} from './types';

const tagTypes = [
  'ViolationAct',
  'ViolationActs',
  'ViolationActDevices',
  'ViolationActModems',
];

export const violationActsApi = injectBmsSlice({
  baseUrl: Config.VITE_API_URL + '/violation-acts',
  tagTypes,
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  endpoints: builder => ({
    getViolationActs: builder.query<ViolationAct[], FiltersGrid | void>({
      query: (params = {}) => {
        return {
          url: `/query`,
          params,
        };
      },
      providesTags: () => ['ViolationAct', 'ViolationActs'],
    }),
    getViolationActsI: builder.infiniteQuery<
      ViolationAct[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['ViolationAct', 'ViolationActs'],
    }),
    getViolationActById: builder.query<ViolationAct, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['ViolationAct'],
    }),
    createViolationActs: builder.mutation<ViolationAct, FormData>({
      query: data => ({
        url: '/',
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        data,
      }),
      invalidatesTags: result => [
        'ViolationAct',
        { type: 'ViolationAct', id: result?.id },
      ],
    }),
    updateViolationActs: builder.mutation<ViolationAct, FormData>({
      query: data => ({
        url: `/${data.get('id')}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: tagTypes,
    }),
    deleteViolationActsById: builder.mutation<ViolationAct, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'ViolationAct',
        { type: 'ViolationAct', id },
      ],
    }),
    getViolationActDevices: builder.query<ViolationActDevice[], string>({
      query: id => ({
        url: `/${id}/devices`,
      }),
      providesTags: () => ['ViolationActDevices'],
    }),
    getViolationActModems: builder.query<ViolationActModem[], string>({
      query: id => ({
        url: `/${id}/modems`,
      }),
      providesTags: () => ['ViolationActModems'],
    }),
    validateCreateViolationAct: builder.mutation<boolean, FormData>({
      query: formData => ({
        url: '/validate-create',
        method: 'POST',
        data: formData,
      }),
    }),
    validateUpdateViolationAct: builder.mutation<boolean, FormData>({
      query: formData => ({
        url: `/${formData.get('id')}/validate-update`,
        method: 'POST',
        data: formData,
      }),
    }),
    validateCreateViolationActDevicesAndModems: builder.mutation<
      boolean,
      FormData
    >({
      query: formData => ({
        url: '/validate-devices-and-modems',
        method: 'POST',
        data: formData,
      }),
    }),
    validateUpdateViolationActDevicesAndModems: builder.mutation<
      boolean,
      FormData
    >({
      query: formData => ({
        url: `/${formData.get('id')}/validate-devices-and-modems`,
        method: 'POST',
        data: formData,
      }),
    }),
    setViolationActResult: builder.mutation<
      string,
      ViolationActResultRequestBody
    >({
      query: data => ({
        url: `/${data.id}/set-result`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: tagTypes,
    }),
    setViolationActDecision: builder.mutation<
      string,
      ViolationActDecisionRequestBody
    >({
      query: data => ({
        url: `/${data.id}/set-decision`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: tagTypes,
    }),
  }),
});

export const {
  useGetViolationActDevicesQuery,
  useGetViolationActsIInfiniteQuery,
  useGetViolationActByIdQuery,
  useCreateViolationActsMutation,
  useUpdateViolationActsMutation,
  useDeleteViolationActsByIdMutation,
  useGetViolationActModemsQuery,
  useValidateCreateViolationActMutation,
  useValidateUpdateViolationActMutation,
  useValidateCreateViolationActDevicesAndModemsMutation,
  useValidateUpdateViolationActDevicesAndModemsMutation,
  useSetViolationActDecisionMutation,
  useSetViolationActResultMutation,
} = violationActsApi;
